import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import {
  newMaterialOfferValidationSchema,
  TO_BE_AGREED,
  IT_DOESNT_HAVE_SPEC,
  IS_CONFIDENTIAL,
  addressFormatter,
} from "../../utils";
import {
  GoBackArrow,
  Loader,
  Modal,
  NewOfferMaterialForm,
  InfoItem,
  FileReader,
  Button,
} from "..";
import { useFileInputUploader, useID } from "../../hooks";
import { CertificationResponse } from "../../types";
import { usePublicCountries } from "../../hooks/useCountries";
import { useMaterial } from "../../hooks/useMaterials";
import {
  useCheckMaterialOffer,
  useCreateMaterialOffer,
} from "../../hooks/useMaterialsOffers";
import { useNotification, useUser } from "../../context";

const Opportunity = () => {
  const { user } = useUser();
  const { openNotification } = useNotification();

  const { t } = useTranslation();
  const id = useID();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [specificationName, setSpecificationName] = useState<any>(null);
  const [certifications, setCertifications] = useState<CertificationResponse[]>(
    []
  );
  const { data: countries } = usePublicCountries();

  const { data: material, isLoading: isLoadingMaterial } = useMaterial(id);

  const {
    data: uploadedSpecificationFileUrl,
    mutate: uploadFile,
    isPending: isUploading,
  } = useFileInputUploader();

  const formOptions = {
    resolver: yupResolver(newMaterialOfferValidationSchema),
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm(formOptions);

  const { data: checkMaterialOffer } = useCheckMaterialOffer(
    id,
    user?.id || "",
    material?.user?.id ?? "",
    material
  );

  const { mutate: createMaterialOffer, isPending: isCreatingMaterialOffer } =
    useCreateMaterialOffer(
      (res) => {
        openNotification(t("materialOffer.success"));
        return navigate(`/material/offer/${res?.id}`);
      },
      () => openNotification(t("newMaterialForm.error"))
    );

  const onSubmit = (data: any) => {
    if (certifications.length === 0) {
      createMaterialOffer({
        ...data,
        specificationFileUrl: uploadedSpecificationFileUrl
          ? uploadedSpecificationFileUrl
          : "",
        material: { id: material?.id },
        origin: { id: data.origin },
        user: { id: user?.id },
      });
    } else {
      const formattedCertfications = certifications.map(
        (cert: CertificationResponse) => ({
          valid: true,
          materialCertification: cert,
        })
      );
      createMaterialOffer({
        ...data,
        specificationFileUrl: uploadedSpecificationFileUrl
          ? uploadedSpecificationFileUrl
          : "",
        material: { id: material?.id },
        origin: { id: data.origin },
        user: { id: user?.id },
        certifications: formattedCertfications,
      });
    }
    setSpecificationName(null);
    reset();
    setOpen(false);
  };
  const onError = () => setOpen(false);

  const handleSpecification = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files;
    if (file) {
      uploadFile(file[0]);
      setSpecificationName(file[0]?.name);
    }
  };

  const handleAddCertification = (cert: CertificationResponse) => {
    setCertifications((prev) => {
      const exists = prev.some((item) => item.id === cert.id);
      if (exists) {
        return prev.filter((item) => item.id !== cert.id);
      } else {
        return [...prev, cert];
      }
    });
  };

  return (
    <Fragment>
      <Modal title={t("materialOffer.apply")} open={open} setOpen={setOpen}>
        <p className="text-xs text-gray-600">
          {t("materialOffer.applyQuestion")}
        </p>
        <div className="flex justify-end mt-4">
          <Button onClick={handleSubmit(onSubmit, onError)}>
            {t("materialOffer.apply")}
          </Button>
        </div>
      </Modal>
      {isLoadingMaterial || isCreatingMaterialOffer ? (
        <Loader isFullScreen />
      ) : (
        <div className="min-h-full flex">
          <div className="flex flex-col w-0 flex-1">
            <div className="relative">
              <GoBackArrow />
              <div className="mx-auto xl:grid xl:grid-cols-2 gap-4">
                <div className="col-span-2 xl:col-span-1 bg-white rounded-md p-4 shadow">
                  <div className="md:flex md:items-center md:justify-between md:space-x-4 border-b pb-4">
                    <div>
                      <h1 className="text-xl font-bold text-gray-900 uppercase">
                        {material?.globalMaterialName}
                      </h1>
                    </div>
                  </div>
                  <Fragment>
                    <InfoItem
                      name={t("opportunities.table.deliveryFormat")}
                      description={material?.deliveryFormat}
                    />
                  </Fragment>
                  <InfoItem
                    name={t("opportunities.table.deliveryPlace")}
                    description={
                      material?.deliveryPlace
                        ? addressFormatter(material?.deliveryPlace?.address)
                        : TO_BE_AGREED
                    }
                  />
                  {material?.brand && (
                    <InfoItem
                      name={t("opportunities.table.brand")}
                      description={material?.brand}
                    />
                  )}
                  {material?.comments && (
                    <InfoItem
                      name={t("opportunities.table.comments")}
                      description={material?.comments}
                    />
                  )}
                  <div className="bg-white overflow-hidden">
                    <div className="py-2 sm:p-0">
                      <dl className="sm:divide-y sm:divide-gray-200">
                        <div className="py-2 sm:grid sm:grid-cols-2 sm:gap-4">
                          <dt className="uppercase text-xxs font-medium text-gray-500">
                            {t("newMaterialForm.specification")}
                          </dt>
                          <dd className="mt-1 text-xxs text-gray-900 sm:mt-0 sm:col-span-1">
                            {material?.confidentSpec ? (
                              <p>{IS_CONFIDENTIAL}</p>
                            ) : material?.fileURL ? (
                              <FileReader fileKey={material.fileURL} />
                            ) : (
                              <p>{IT_DOESNT_HAVE_SPEC}</p>
                            )}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="col-span-2 xl:col-span-1">
                  {!checkMaterialOffer && material ? (
                    <NewOfferMaterialForm
                      countries={countries}
                      material={material}
                      errors={errors}
                      setOpen={setOpen}
                      register={register}
                      isLoadingUpload={isUploading}
                      handleAddCertification={handleAddCertification}
                      specificationName={specificationName}
                      handleSpecification={handleSpecification}
                    />
                  ) : (
                    <div className="w-full text-xs p-2 rounded border border-green-800 bg-green-100 text-center font-bold uppercase">
                      <p>{t("materialOffer.applied")}!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default Opportunity;
